import { useLexicalComposerContext } from "@lexical/react/LexicalComposerContext";
import { Stack, Typography } from "@mui/material";
import { useCallback } from "react";

import { PickerPopover } from "./PickerPopover";
import { PickerRow } from "./PickerRow";
import { INSERT_MERGE_TAG_COMMAND } from "../../extensions/mergeTags/editor/mergeTagPlugin";
import type { MergeTagCategory, ResolvedFilters } from "@/services/mergeTags";

interface MergeTagPickerPopoverProps {
  anchorEl: HTMLElement | null;
  onClose: () => void;
  /** Resolved categories to offer (already surface-filtered + narrowed). */
  categories: MergeTagCategory[];
}

export function MergeTagPickerPopover({
  anchorEl,
  onClose,
  categories,
}: MergeTagPickerPopoverProps) {
  const [editor] = useLexicalComposerContext();

  const handleInsert = useCallback(
    (identifier: string, label: string, defaultFilters?: ResolvedFilters) => {
      onClose();
      editor.focus(() => {
        editor.dispatchCommand(INSERT_MERGE_TAG_COMMAND, {
          tag: identifier,
          label,
          filters: defaultFilters,
        });
      });
    },
    [editor, onClose],
  );

  return (
    <PickerPopover
      anchorEl={anchorEl}
      onClose={onClose}
      paperSx={{ overflow: "auto", p: 2 }}
    >
      <Stack gap={2}>
        {categories.map((category) => (
          <Stack key={category.id} gap={1}>
            <Typography
              variant="caption"
              color="text.secondary"
              fontWeight={600}
              sx={{ px: 2, textTransform: "uppercase", letterSpacing: 0.5 }}
            >
              {category.label}
            </Typography>
            {category.tags.map((tag) => (
              <PickerRow
                key={tag.identifier}
                primary={tag.label}
                secondary={tag.secondary ?? `{{${tag.identifier}}}`}
                onSelect={() =>
                  handleInsert(tag.identifier, tag.label, tag.defaultFilters)
                }
              />
            ))}
          </Stack>
        ))}
      </Stack>
    </PickerPopover>
  );
}
